import * as React from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { useServerSearch } from '../../hooks/useServerSearch';
import { customerLabel } from '../../utils/customer';

// A block's already-assigned work order must stay visible/selectable even when it is NOT in the
// current search results — e.g. it moved to waiting (auto-pause on clock stop), completed, or
// picked_up after the time was logged. Built from the block's own maintenance_info snapshot.
const optionFromBlock = (block) => {
  const info = block.maintenance_info || {};
  return {
    id: block.maintenance,
    vehicle: info.vehicle_registration ? { registration_number: info.vehicle_registration } : null,
    customer: info.customer_name ? { name: info.customer_name } : null,
    description: info.description || '',
    state_display: info.state_display || '',
    _synthetic: true,
  };
};

/**
 * Work-order picker for a single work-time block. Uses server-side search against
 * /api/maintenances/ so ANY work order can be found by registration number or work number,
 * regardless of its state (open, completed, picked_up, ...). The previous client-side filter
 * only saw the first page of active-state orders, so already-picked-up orders were unfindable.
 */
const MaintenanceBlockSelect = ({ t, block, onChange }) => {
  const { items, autocompleteProps } = useServerSearch('/api/maintenances/');

  const currentValue =
    items.find((m) => m.id === block.maintenance) ||
    (block.maintenance ? optionFromBlock(block) : null);

  const options =
    block.maintenance && !items.some((m) => m.id === block.maintenance)
      ? [optionFromBlock(block), ...items]
      : items;

  return (
    <Autocomplete
      value={currentValue}
      onChange={(event, newValue) => onChange(newValue?.id || null)}
      options={options}
      getOptionLabel={(option) => {
        const parts = [];
        if (option.vehicle?.registration_number) {
          parts.push(option.vehicle.registration_number);
        }
        if (option.description) {
          parts.push(option.description);
        }
        if (option.customer?.name) {
          parts.push(`(${option.customer.name})`);
        }
        return parts.join(' - ') || `Maintenance #${option.id}`;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoComplete="off"
          label={t('Maintenance') + ' *'}
          placeholder={t('Search maintenances...')}
          size="small"
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
              {option.vehicle?.registration_number || t('No vehicle')}
              {option.description && ` - ${option.description}`}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              {customerLabel(option.customer) || t('No customer')} • {option.state_display}
            </Typography>
          </Box>
        </Box>
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={t('No maintenances found')}
      loadingText={t('Loading...')}
      size="small"
      {...autocompleteProps}
    />
  );
};

export default MaintenanceBlockSelect;
